import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { categories } from "@/mocks/categories";

const funMessages = [
  "Oops! This quiz doesn't exist... yet!",
  "404: The page ran away to play games!",
  "Looks like this page failed the quiz!",
  "This URL got a big fat ZERO on the test!",
  "Page not found — but your next quiz is!",
];

const categoryShortLabels: Record<string, string> = {
  rpg: "RPG",
  sandbox: "Sandbox",
  action: "Action",
  entertainment: "Fun",
  movies: "Movies",
  anime: "Anime",
  love: "Love",
  animals: "Animals",
};

export default function NotFound() {
  const [msgIndex, setMsgIndex] = useState(0);
  const [score, setScore] = useState(0);
  const [clicked, setClicked] = useState(false);
  const [floatPos, setFloatPos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    const interval = setInterval(() => {
      setMsgIndex((prev) => (prev + 1) % funMessages.length);
    }, 2800);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    const move = () => {
      setFloatPos({
        x: Math.sin(Date.now() / 1200) * 12,
        y: Math.cos(Date.now() / 900) * 8,
      });
    };
    const raf = setInterval(move, 50);
    return () => clearInterval(raf);
  }, []);

  const handleBonk = () => {
    setClicked(true);
    setScore((s) => s + 10);
    setTimeout(() => setClicked(false), 300);
  };

  const featuredCats = categories.slice(0, 4);

  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center px-4 py-16 relative overflow-hidden"
      style={{ background: "linear-gradient(135deg, #fff9f5 0%, #fff0f0 50%, #fff5f0 100%)" }}
    >
      {/* Decorative background blobs */}
      <div className="absolute top-0 left-0 w-72 h-72 rounded-full opacity-20 pointer-events-none"
        style={{ background: "radial-gradient(circle, #FF6B6B 0%, transparent 70%)", transform: "translate(-30%, -30%)" }} />
      <div className="absolute bottom-0 right-0 w-96 h-96 rounded-full opacity-15 pointer-events-none"
        style={{ background: "radial-gradient(circle, #FF8E53 0%, transparent 70%)", transform: "translate(30%, 30%)" }} />
      <div className="absolute top-1/2 left-10 w-48 h-48 rounded-full opacity-10 pointer-events-none"
        style={{ background: "radial-gradient(circle, #FFB347 0%, transparent 70%)" }} />

      {/* Floating stars */}
      {[...Array(6)].map((_, i) => (
        <div
          key={i}
          className="absolute pointer-events-none select-none text-[#FF6B6B] opacity-20"
          style={{
            top: `${10 + i * 14}%`,
            left: `${5 + i * 15}%`,
            fontSize: `${14 + (i % 3) * 8}px`,
            transform: `rotate(${i * 30}deg)`,
            animation: `spin ${3 + i}s linear infinite`,
          }}
        >
          ✦
        </div>
      ))}

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center max-w-2xl w-full">

        {/* Logo link */}
        <Link to="/" className="flex items-center gap-2 mb-10 cursor-pointer group">
          <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53]">
            <i className="ri-gamepad-fill text-white text-base" />
          </div>
          <span className="font-extrabold text-2xl tracking-tight text-gray-900" style={{ fontFamily: "'Nunito', sans-serif" }}>
            Funs<span className="text-[#FF6B6B]">Test</span>
          </span>
        </Link>

        {/* Giant 404 */}
        <div className="relative mb-2 select-none">
          <span
            className="text-[9rem] md:text-[13rem] font-black leading-none"
            style={{
              fontFamily: "'Nunito', sans-serif",
              background: "linear-gradient(135deg, #FF6B6B 0%, #FF8E53 50%, #FFB347 100%)",
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              backgroundClip: "text",
              filter: "drop-shadow(0 4px 0px rgba(255,107,107,0.2))",
            }}
          >
            404
          </span>

          {/* Floating game controller */}
          <button
            onClick={handleBonk}
            className="absolute -top-4 -right-6 md:-right-10 cursor-pointer focus:outline-none"
            style={{
              transform: `translate(${floatPos.x}px, ${floatPos.y}px) ${clicked ? "scale(0.85) rotate(-15deg)" : "scale(1) rotate(0deg)"}`,
              transition: clicked ? "transform 0.1s ease" : "transform 0.05s linear",
            }}
            title="Bonk!"
          >
            <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-gradient-to-br from-[#FF6B6B] to-[#FF8E53] shadow-lg">
              <i className="ri-gamepad-fill text-white text-2xl md:text-3xl" />
            </div>
            {clicked && (
              <span className="absolute -top-6 left-1/2 -translate-x-1/2 text-[#FF6B6B] font-black text-lg animate-bounce whitespace-nowrap">
                +10 pts!
              </span>
            )}
          </button>
        </div>

        {/* Score badge */}
        {score > 0 && (
          <div className="mb-3 px-4 py-1.5 bg-[#FF6B6B]/10 border border-[#FF6B6B]/20 rounded-full text-sm font-bold text-[#FF6B6B]">
            <i className="ri-trophy-line mr-1.5" />
            Score: {score} pts — keep bonking!
          </div>
        )}

        {/* Rotating fun message */}
        <div className="h-8 mb-3 overflow-hidden">
          <p
            key={msgIndex}
            className="text-base md:text-lg font-bold text-[#FF6B6B]"
            style={{ animation: "fadeSlideIn 0.4s ease" }}
          >
            {funMessages[msgIndex]}
          </p>
        </div>

        <p className="text-gray-500 text-sm md:text-base mb-8 max-w-md leading-relaxed">
          The page you&apos;re looking for went on a quest and never came back.
          Don&apos;t worry — there are plenty of awesome quizzes waiting for you!
        </p>

        {/* CTA buttons */}
        <div className="flex flex-col sm:flex-row items-center gap-3 mb-12">
          <Link
            to="/"
            className="px-7 py-3 bg-gradient-to-r from-[#FF6B6B] to-[#FF8E53] text-white font-bold rounded-full text-sm hover:opacity-90 transition-opacity cursor-pointer whitespace-nowrap shadow-md"
          >
            <i className="ri-home-4-line mr-2" />
            Back to Home
          </Link>
          <Link
            to="/categories"
            className="px-7 py-3 bg-white border-2 border-[#FF6B6B]/30 text-[#FF6B6B] font-bold rounded-full text-sm hover:bg-[#FF6B6B]/5 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-gamepad-line mr-2" />
            Browse All Quizzes
          </Link>
        </div>

        {/* Divider */}
        <div className="w-full flex items-center gap-4 mb-8">
          <div className="flex-1 h-px bg-gray-200" />
          <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider whitespace-nowrap">
            Or jump to a category
          </span>
          <div className="flex-1 h-px bg-gray-200" />
        </div>

        {/* Category quick links */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 w-full">
          {featuredCats.map((cat) => (
            <Link
              key={cat.id}
              to={`/categories?cat=${cat.slug}`}
              className="flex flex-col items-center gap-2 p-4 bg-white rounded-2xl border border-gray-100 hover:border-[#FF6B6B]/30 hover:shadow-sm transition-all cursor-pointer group"
            >
              <span className={`w-10 h-10 flex items-center justify-center rounded-xl bg-gradient-to-br ${cat.color}`}>
                <i className={`${cat.icon} text-white text-base`} />
              </span>
              <span className="text-xs font-bold text-gray-700 group-hover:text-[#FF6B6B] transition-colors">
                {categoryShortLabels[cat.id] ?? cat.label}
              </span>
              <span className="text-[10px] text-gray-400">{cat.quizCount} quizzes</span>
            </Link>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
}
